import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/apiService';
import AddClassModal, { ClassFormData } from '../components/AddClassModal';
import { ChartBarIcon, DocumentChartBarIcon, UsersIcon, ArrowRightIcon } from '@heroicons/react/24/outline';

interface Class {
  id: string;
  name: string;
  code: string;
  term: string;
  students: number;
  hasFeedbackSetup: boolean;
}


const Dashboard = () => {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [showAddClassModal, setShowAddClassModal] = useState(false);
  const [classes, setClasses] = useState<Class[]>([]);
  const [loading, setLoading] = useState(true);

  // Load professor's classes on component mount
  useEffect(() => {
    const loadClasses = async () => {
      console.log('Dashboard: Loading classes - user:', user?.id, 'profile:', profile);
      const userType = profile?.userType || profile?.user_type;
      if (!user || !profile || userType !== 'professor') {
        console.log('Dashboard: Skipping class load - missing user/profile or not professor');
        setLoading(false);
        return;
      }
      
      console.log('Dashboard: Starting to load classes for professor:', user.id);
      setLoading(true);
      try {
        const { data, error } = await apiService.getClassesByProfessor(user.id);
        console.log('Dashboard: Classes loaded:', data, 'error:', error);
        
        if (error) {
          throw error;
        }
        
        if (data) {
          console.log('Dashboard: Processing', data.length, 'classes');
          const formattedClasses: Class[] = await Promise.all(data.map(async cls => {
            // Get actual student count for each class
            const { data: enrollments } = await apiService.getClassStudents(cls.id);
            const studentCount = enrollments ? enrollments.length : 0;
            
            // Check if feedback setup exists
            const { data: setup } = await apiService.getClassSetup(cls.id);
            const hasFeedbackSetup = !!setup;
            
            return {
              id: cls.id,
              name: cls.name,
              code: cls.code,
              term: cls.term,
              students: studentCount,
              hasFeedbackSetup,
            };
          }));
          console.log('Dashboard: Formatted classes:', formattedClasses);
          setClasses(formattedClasses);
        }
      } catch (error) {
        console.error('Dashboard: Failed to load classes:', error);
      } finally {
        console.log('Dashboard: Loading complete');
        setLoading(false);
      }
    };

    loadClasses();
  }, [user, profile]);

  const handleAddClass = async (classData: ClassFormData) => {
    if (!user) return;
    
    try {
      const { data, error } = await apiService.createClass({
        name: classData.name,
        code: classData.code,
        term: classData.term,
        professor_id: user.id,
      });

      if (error) {
        console.error('Failed to create class:', error);
        return;
      }

      if (data) {
        const newClass: Class = {
          id: data.id,
          name: data.name,
          code: data.code,
          term: data.term,
          students: 0, // New class starts with 0 students
          hasFeedbackSetup: false, // New class has no setup yet
        };
        setClasses(prev => [...prev, newClass]);
      }
    } catch (error) {
      console.error('Failed to create class:', error);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Welcome Section */}
      <div className="mb-8 p-6 bg-gradient-to-r from-emerald-50 to-lime-50 rounded-xl border border-emerald-100">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Welcome back, {profile?.full_name || 'Professor'}!
        </h1>
        <p className="text-gray-600">
          Manage your classes and connect with student needs through intelligent feedback.
        </p>
        <div className="mt-4 flex items-center space-x-4 text-sm text-gray-500">
          <span>📧 {user?.email}</span>
          <span>👨‍🏫 Professor</span>
          <span>📚 {classes.length} Classes</span>
        </div>
      </div>

      <div className="flex justify-between items-center mb-8">
        <h2 className="text-2xl font-bold text-black">My Classes</h2>
        <button 
          onClick={() => setShowAddClassModal(true)}
          className="px-4 py-2 bg-white text-emerald-600 rounded-lg hover:bg-emerald-50 transition-all duration-300 shadow-lg"
        >
          Add New Class
        </button>
      </div>

      {classes.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📚</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No classes yet</h3>
          <p className="text-gray-600 mb-6">Get started by creating your first class!</p>
          <button
            onClick={() => setShowAddClassModal(true)}
            className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-lg hover:opacity-90 transition-all duration-300 shadow-lg"
          >
            Create Your First Class
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {classes.map((classItem) => (
          <div
            key={classItem.id}
            className="relative group"
          >
            <div className="absolute -inset-0.5 bg-gradient-to-r from-lime-500 to-green-600 rounded-lg blur opacity-25 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>
            <div className="relative bg-white/90 backdrop-blur-sm rounded-lg p-6 ring-1 ring-emerald-500/10">
              {/* Class Header */}
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-emerald-600">{classItem.code}</span>
                <span className="text-sm text-gray-500">{classItem.term}</span>
              </div>
              
              {/* Class Title */}
              <h2 className="text-xl font-semibold text-gray-900 mb-3 line-clamp-2">{classItem.name}</h2>
              
              {/* Class Stats */}
              <div className="flex items-center justify-between mb-4 text-sm text-gray-600">
                <div className="flex items-center">
                  <UsersIcon className="h-4 w-4 mr-1" />
                  <span>{classItem.students} Students</span>
                </div>
                {classItem.hasFeedbackSetup && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Feedback Active
                  </span>
                )}
              </div>
              
              {/* Quick Actions */}
              <div className="space-y-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/dashboard/class/${classItem.id}`);
                  }}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors group/action"
                >
                  <div className="flex items-center">
                    <ArrowRightIcon className="h-4 w-4 mr-2 text-emerald-600" />
                    View Overview
                  </div>
                  <ArrowRightIcon className="h-3 w-3 text-gray-400 group-hover/action:text-gray-600" />
                </button>
                
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/dashboard/class/${classItem.id}/insights`);
                  }}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors group/action"
                >
                  <div className="flex items-center">
                    <ChartBarIcon className="h-4 w-4 mr-2 text-blue-600" />
                    View Insights
                  </div>
                  <ArrowRightIcon className="h-3 w-3 text-gray-400 group-hover/action:text-gray-600" />
                </button>
                
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/dashboard/class/${classItem.id}/feedback`);
                  }}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors group/action"
                >
                  <div className="flex items-center">
                    <DocumentChartBarIcon className="h-4 w-4 mr-2 text-purple-600" />
                    Manage Feedback
                  </div>
                  <ArrowRightIcon className="h-3 w-3 text-gray-400 group-hover/action:text-gray-600" />
                </button>
              </div>
            </div>
          </div>
          ))}
        </div>
      )}

      <AddClassModal
        isOpen={showAddClassModal}
        onClose={() => setShowAddClassModal(false)}
        onSubmit={handleAddClass}
      />
    </div>
  );
};

export default Dashboard;